import React, { useMemo, useState, useRef } from 'react';
import getConfig from 'next/config';
import { GetServerSideProps } from 'next';
import { getData, overwrite } from 'country-list';
import {
    Row,
    Col,
    Select,
    Input,
    Divider,
    Space,
    Radio,
    Form,
    Checkbox,
    Button,
    Modal,
    Alert
} from 'antd';
import parse from 'html-react-parser';
import styled from 'styled-components';
import BasicLayout from 'layout/Basic';
import Section, { SectionTitle } from 'components/Section';
import { convertProps } from 'lib/parseAPIResponse';
import RodoText from 'components/RodoText';
import { SkyscraperMobile } from 'components/Ads';
import { merriweather } from 'styles/GlobalStyle';
import LogoHeadingSection from 'components/LogoHeadingSection';

const Text = styled.span`
    font-family: ${merriweather.style.fontFamily};
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    color: ${(props) => props.color || props.theme.colors.brownishGrey};
`;

const CompanyName = styled.h1`
    font-size: 32px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.brownishGrey};
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const RightCol = styled(Col)`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    padding: 10px;
    color: ${({ theme }) => theme.colors.brownishGrey};
    p {
        margin: 0;
    }
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const LeftCol = styled(Col)`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: right;
    padding: 10px;
    color: ${({ theme }) => theme.colors.brownishGrey};
    p {
        margin: 0;
    }
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const AddressTitle = styled.p`
    font-weight: normal;
    color: ${({ theme }) => theme.colors.primary};
    p {
        margin-bottom: 0;
    }
`;

const SubTitle = styled.p`
    font-family: ${merriweather.style.fontFamily};
    font-size: 18px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 2.22;
    letter-spacing: 0.36px;
    text-align: left;
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const SmallCheckboxText = styled.span`
    font-size: 14px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.greyishBrown};
`;

const SubscriptionTypeText = styled.span`
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const RodoTextSpan = styled.span`
    font-size: 14px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.greyishBrown};
    > button {
        font-size: 14px;
        font-weight: 300;
    }
`;

const BranchName = styled.p`
    color: ${({ theme }) => theme.colors.primary};
    margin: 0;
`;

const AlertWrapper = styled.div`
    padding-bottom: 35px;
`;

const RequiredText = styled.p`
    font-size: 14px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.86;
    letter-spacing: normal;
    text-align: right;
    color: ${({ theme }) => theme.colors.primary};
`;

interface Props {
    subscription: {
        postalAddress: string;
        about: string;
        yearSubscriptionName: string;
        halfYearSubscriptionName: string;
        halfYearSubscriptionPrice: number;
        yearSubscriptionPrice: number;
        confirmation: string;
    };
    issues: Issue[];
    email: string;
    phone: string;
    fax: string;
    ads: PageAds;
}

export const SearchPage = ({
    subscription,
    issues,
    email,
    phone,
    fax,
    ads
}: Props): JSX.Element => {
    const countries = useMemo(() => {
        overwrite([
            {
                code: 'PL',
                name: 'Polska'
            }
        ]);
        return getData();
    }, []);

    const [messageIsSending, setMessageIsSending] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState(false);
    const [invoiceAlternativeAddress, setInvoiceAlternativeAddress] = useState(false);
    const prenumerataRef = useRef(null);

    const {
        postalAddress,
        about,
        yearSubscriptionName,
        halfYearSubscriptionName,
        halfYearSubscriptionPrice,
        yearSubscriptionPrice,
        confirmation
    } = subscription;

    function onChange(e) {
        setInvoiceAlternativeAddress(e.target.checked);
    }

    const showModal = () => {
        Modal.info({
            okText: 'Zamknij',
            width: '80%',
            title: 'Informacja o przetwarzaniu danych osobowych',
            content: <RodoText />
        });
    };

    const { name, apiUrl } = getConfig().publicRuntimeConfig;
    const onFinish = (values) => {
        setMessageIsSending(true);
        fetch(`${apiUrl}/public/api/subscription`, {
            method: 'post',
            headers: {
                Accept: 'application/json, text/plain, */*',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(values)
        })
            .then((response) => {
                setMessageIsSending(false);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                return response;
            })
            .then((response) => {
                setIsSubscribed(true);
                return response;
            })
            .catch(() => {
                setSubscriptionError(true);
            })
            .finally(() => {
                prenumerataRef.current.scrollIntoView();
                setMessageIsSending(false);
            });
    };
    const { skyscraper } = ads;
    return (
        <BasicLayout ads={ads}>
            <LogoHeadingSection />
            <Section>
                <Col xs={24} md={{ span: 17, offset: 7 }}>
                    <Row>
                        <RightCol xs={24} md={14}>
                            <CompanyName>{name}</CompanyName>
                            <BranchName>Dział prenumeraty</BranchName>
                            {phone && (
                                <p style={{ margin: 0, whiteSpace: 'nowrap' }}>tel. {phone}</p>
                            )}
                            {fax && <p style={{ margin: 0, whiteSpace: 'nowrap' }}>faks: {fax}</p>}
                            {email && (
                                <p style={{ margin: 0, whiteSpace: 'nowrap' }}>e-mail: {email}</p>
                            )}
                        </RightCol>
                        <LeftCol xs={24} md={10}>
                            <AddressTitle>Dane adresowe</AddressTitle>
                            {parse(postalAddress)}
                        </LeftCol>
                    </Row>
                </Col>
            </Section>
            <Row>
                <Col span={24} ref={prenumerataRef}>
                    <Divider />
                </Col>
            </Row>
            <Row>
                <Col xs={24} md={12} lg={0}>
                    <SkyscraperMobile id={skyscraper[0]} margin="25px auto 0 auto" />
                </Col>
                <Col xs={24} md={12} lg={0}>
                    <SkyscraperMobile id={skyscraper[1]} margin="25px auto 0 auto" />
                </Col>
            </Row>
            <Section>
                <Col xs={24} md={7}>
                    <SectionTitle title="Prenumerata" level={2} />
                </Col>
                <Col xs={24} md={12}>
                    {isSubscribed && (
                        <AlertWrapper>
                            <Alert
                                message="Dziękujemy za zamówienie prenumeraty"
                                description={parse(confirmation)}
                                type="success"
                                showIcon
                            />
                        </AlertWrapper>
                    )}
                    {subscriptionError && (
                        <AlertWrapper>
                            <Alert
                                message="Przepraszamy"
                                description="Wystąpił nieoczekiwany błąd, spróbuj ponownie później."
                                type="error"
                                showIcon
                            />
                        </AlertWrapper>
                    )}
                    <Text>{parse(about)}</Text>
                </Col>
                <Col span={24}>
                    <Divider />
                </Col>
            </Section>
            <Form
                initialValues={{ firstIssue: issues[0].seoSlug }}
                validateMessages={{ required: 'Pole nie może być puste' }}
                onFinish={onFinish}
                size="large"
                scrollToFirstError
                onValuesChange={() => {
                    setIsSubscribed(false);
                    setSubscriptionError(false);
                }}
            >
                <Section>
                    <Col xs={24} md={7}>
                        <SectionTitle title="Zamawiam" level={2} />
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="subscriptionType"
                            rules={[
                                { required: true, message: 'Zaznaczenie pola jest obowiązkowe' }
                            ]}
                        >
                            <Radio.Group>
                                <Space direction="vertical">
                                    <Radio value={2}>
                                        <SubscriptionTypeText>
                                            {halfYearSubscriptionName} - {halfYearSubscriptionPrice}{' '}
                                            PLN
                                        </SubscriptionTypeText>
                                    </Radio>
                                    <Radio value={1}>
                                        <SubscriptionTypeText>
                                            {yearSubscriptionName} - {yearSubscriptionPrice} PLN
                                        </SubscriptionTypeText>
                                    </Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            name="firstIssue"
                            label="Od numeru"
                            rules={[
                                { required: true, message: 'Wybranie numeru jest obowiązkowe' }
                            ]}
                        >
                            <Select
                                style={{ width: '100%' }}
                                options={issues.map((issue) => ({
                                    label: `${issue.number}/${issue.year}`,
                                    value: issue.seoSlug
                                }))}
                            >
                                {}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Divider />
                    </Col>
                    <Col span={24}></Col>
                </Section>
                <Section>
                    <Col xs={24} md={7}>
                        <SectionTitle title="DANE ZAMAWIAJĄCEGO" level={2} />
                    </Col>
                    <Col xs={24} md={12}>
                        <Row>
                            <Col xs={24} md={24}>
                                <SubTitle>DANE DO FAKTURY</SubTitle>
                                <Form.Item
                                    name="invoiceFirstName"
                                    rules={[{ required: true, message: 'Wprowadź poprawne imię' }]}
                                >
                                    <Input placeholder="Imię*" />
                                </Form.Item>
                                <Form.Item
                                    name="invoiceLastName"
                                    rules={[
                                        { required: true, message: 'Wprowadź poprawne nazwisko' }
                                    ]}
                                >
                                    <Input placeholder="Nazwisko*" />
                                </Form.Item>
                                <Form.Item name="invoiceCompanyName">
                                    <Input placeholder="Firma" />
                                </Form.Item>
                                <Form.Item name="invoiceNip">
                                    <Input placeholder="Nip" />
                                </Form.Item>
                                <Form.Item name="invoiceStreet" rules={[{ required: true }]}>
                                    <Input placeholder="Ulica*" />
                                </Form.Item>
                                <Form.Item style={{ marginBottom: 0 }}>
                                    <Form.Item
                                        name="invoiceBuildingNumber"
                                        rules={[{ required: true }]}
                                        style={{
                                            display: 'inline-block',
                                            width: 'calc(50% - 8px)'
                                        }}
                                    >
                                        <Input placeholder="Numer budynku*" />
                                    </Form.Item>
                                    <Form.Item
                                        name="invoiceFlatNumber"
                                        style={{
                                            display: 'inline-block',
                                            width: 'calc(50%)',
                                            margin: '0 0 0 8px'
                                        }}
                                    >
                                        <Input placeholder="Numer lokalu" />
                                    </Form.Item>
                                </Form.Item>
                                <Form.Item style={{ marginBottom: 0 }} rules={[{ required: true }]}>
                                    <Form.Item
                                        name="invoiceZip"
                                        rules={[{ required: true }]}
                                        style={{
                                            display: 'inline-block',
                                            width: 'calc(50% - 8px)'
                                        }}
                                    >
                                        <Input placeholder="Kod pocztowy*" />
                                    </Form.Item>
                                    <Form.Item
                                        name="invoiceCity"
                                        rules={[{ required: true }]}
                                        style={{
                                            display: 'inline-block',
                                            width: 'calc(50%)',
                                            margin: '0 0 0 8px'
                                        }}
                                    >
                                        <Input placeholder="Miejscowość*" />
                                    </Form.Item>
                                </Form.Item>
                                <Form.Item name="invoiceCountry" rules={[{ required: true }]}>
                                    <Select
                                        optionFilterProp="label"
                                        showSearch
                                        placeholder="Kraj*"
                                        style={{ width: '100%' }}
                                        options={countries.map((country) => ({
                                            label: country.name,
                                            value: country.code
                                        }))}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Section>
                                    <Col span={24}>
                                        <SubTitle>DANE DO WYSYŁKI</SubTitle>
                                        <Form.Item
                                            name="invoiceAlternativeAddress"
                                            valuePropName="checked"
                                        >
                                            <Checkbox onChange={onChange}>
                                                <SmallCheckboxText>
                                                    Zaznacz jeśli różnią się od danych zamawiającego
                                                </SmallCheckboxText>
                                            </Checkbox>
                                        </Form.Item>

                                        {invoiceAlternativeAddress && (
                                            <>
                                                <Form.Item
                                                    name="receiverFirstName"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Wprowadź poprawne imię'
                                                        }
                                                    ]}
                                                >
                                                    <Input placeholder="Imię*" />
                                                </Form.Item>
                                                <Form.Item
                                                    name="receiverLastName"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Wprowadź poprawne nazwisko'
                                                        }
                                                    ]}
                                                >
                                                    <Input placeholder="Nazwisko*" />
                                                </Form.Item>
                                                <Form.Item name="receiverCompanyName">
                                                    <Input placeholder="Firma" />
                                                </Form.Item>
                                                <Form.Item name="receiverNip">
                                                    <Input placeholder="Nip" />
                                                </Form.Item>
                                                <Form.Item
                                                    name="receiverStreet"
                                                    rules={[{ required: true }]}
                                                >
                                                    <Input placeholder="Ulica*" />
                                                </Form.Item>
                                                <Form.Item style={{ marginBottom: 0 }}>
                                                    <Form.Item
                                                        name="receiverBuildingNumber"
                                                        rules={[{ required: true }]}
                                                        style={{
                                                            display: 'inline-block',
                                                            width: 'calc(50% - 8px)'
                                                        }}
                                                    >
                                                        <Input placeholder="Numer budynku*" />
                                                    </Form.Item>
                                                    <Form.Item
                                                        name="receiverFlatNumber"
                                                        style={{
                                                            display: 'inline-block',
                                                            width: 'calc(50%)',
                                                            margin: '0 0 0 8px'
                                                        }}
                                                    >
                                                        <Input placeholder="Numer lokalu" />
                                                    </Form.Item>
                                                </Form.Item>
                                                <Form.Item style={{ marginBottom: 0 }}>
                                                    <Form.Item
                                                        name="receiverZip"
                                                        rules={[{ required: true }]}
                                                        style={{
                                                            display: 'inline-block',
                                                            width: 'calc(50% - 8px)'
                                                        }}
                                                    >
                                                        <Input placeholder="Kod pocztowy*" />
                                                    </Form.Item>
                                                    <Form.Item
                                                        name="receiverCity"
                                                        rules={[{ required: true }]}
                                                        style={{
                                                            display: 'inline-block',
                                                            width: 'calc(50%)',
                                                            margin: '0 0 0 8px'
                                                        }}
                                                    >
                                                        <Input placeholder="Miejscowość*" />
                                                    </Form.Item>
                                                </Form.Item>
                                                <Form.Item
                                                    name="receiverCountry"
                                                    rules={[{ required: true }]}
                                                >
                                                    <Select
                                                        optionFilterProp="label"
                                                        showSearch
                                                        placeholder="Kraj*"
                                                        style={{ width: '100%' }}
                                                        options={countries.map((country) => ({
                                                            label: country.name,
                                                            value: country.code
                                                        }))}
                                                    />
                                                </Form.Item>
                                            </>
                                        )}
                                    </Col>
                                </Section>
                            </Col>
                        </Row>
                    </Col>
                    <Col span={24}>
                        <Divider />
                    </Col>
                </Section>
                <Section>
                    <Col xs={24} md={7}>
                        <SectionTitle title="DANE KONTAKTOWE" level={2} />
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="contactFirstName"
                            rules={[{ required: true, message: 'Wprowadź poprawne imię' }]}
                        >
                            <Input placeholder="Imię*" />
                        </Form.Item>
                        <Form.Item
                            name="contactLastName"
                            rules={[{ required: true, message: 'Wprowadź poprawne nazwisko' }]}
                        >
                            <Input placeholder="Nazwisko*" />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0 }} rules={[{ required: true }]}>
                            <Form.Item
                                name="contactPhone"
                                rules={[{ required: true }]}
                                style={{
                                    display: 'inline-block',
                                    width: 'calc(50% - 8px)'
                                }}
                            >
                                <Input placeholder="Telefon*" />
                            </Form.Item>
                            <Form.Item
                                name="contactFax"
                                style={{
                                    display: 'inline-block',
                                    width: 'calc(50%)',
                                    margin: '0 0 0 8px'
                                }}
                            >
                                <Input placeholder="Faks" />
                            </Form.Item>
                        </Form.Item>
                        <Form.Item
                            name="contactEmail"
                            rules={[
                                { required: true },
                                { type: 'email', message: 'Podaj prawidłowy adres e-mail' }
                            ]}
                        >
                            <Input placeholder="Email*" />
                        </Form.Item>
                        <Form.Item
                            name="agreement1"
                            valuePropName="checked"
                            rules={[
                                { required: true, message: 'Zaznaczenie pola jest obowiązkowe' }
                            ]}
                        >
                            <Checkbox>
                                <SmallCheckboxText>
                                    Wyrażam zgodę na otrzymywanie informacji handlowej kierowanej do
                                    mnie za pomocą środków komunikacji elektronicznej w
                                    szczególności poczty elektronicznej zgodnie z przepisem art. 10
                                    ust 2 ustawy z dnia 18 lipca 2002 roku o świadczeniu usług drogą
                                    elektroniczną (Dz. U. 144 z 2002 r. poz. 1204)*
                                </SmallCheckboxText>
                            </Checkbox>
                        </Form.Item>
                        <Form.Item
                            name="agreement2"
                            valuePropName="checked"
                            rules={[
                                { required: true, message: 'Zaznaczenie pola jest obowiązkowe' }
                            ]}
                        >
                            <Checkbox>
                                <SmallCheckboxText>
                                    Wyrażam zgodę na zachowanie i przetwarzanie moich danych
                                    osobowych przez Raven Media Sp. z o.o. zgodnie z Ustawą o
                                    ochronie danych osobowych (Dz.U. nr 133, póź. 883)*
                                </SmallCheckboxText>
                            </Checkbox>
                        </Form.Item>
                        <RequiredText>* Pola obowiązkowe</RequiredText>
                    </Col>
                </Section>
                <Section>
                    <Col xs={24} md={{ offset: 7, span: 12 }}>
                        <RodoTextSpan>
                            RAVEN MEDIA sp. z o.o. informuje, że będzie administratorem dotyczących
                            Pani/Pana danych osobowych podanych w niniejszym formularzu i w toku
                            realizacji zamówionych w formularzu usług.
                            <Button
                                onClick={showModal}
                                type="link"
                                style={{ padding: '0 5px', height: 'auto' }}
                            >
                                Więcej...
                            </Button>
                        </RodoTextSpan>
                    </Col>
                </Section>
                <Section justify="center">
                    <Col>
                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={messageIsSending}
                                disabled={isSubscribed}
                            >
                                Zamawiam
                            </Button>
                        </Form.Item>
                    </Col>
                </Section>
            </Form>
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async () => {
    const { apiUrl } = getConfig().publicRuntimeConfig;

    return Promise.all([
        fetch(`${apiUrl}/public/api/subscription`),
        fetch(`${apiUrl}/public/api/naszadsik?path=prenumerata`)
    ]).then(async ([res1, res2]) => {
        const data = await res1.json();
        if (data.code === 404) {
            return {
                notFound: true
            };
        }
        const ads = await res2.json();
        return { props: { ...convertProps(ads), ...convertProps(data) } };
    });
};

export default SearchPage;
